<template>
    <div class="modal fade" id="createBusinessDirectoryModal" ref="createBusinessDirectoryModal" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog" :class="mediaLibOpened ? 'modal-xl' : 'modal-lg'">
            <div class="modal-content">
                <div class="modal-header tw-red-bg-color text-white">
                    <div class="d-flex">
                        <span class="tw-create-modal-icon d-inline-flex justify-content-center align-items-center tw-light-red-bg-color me-3">
                            <i class="fas fa-plus"></i>
                        </span>
                        <span class="d-inline-flex">
                            <div v-if="!editing" class="row">
                                <h5 class="modal-title col-12">Create Business Directory</h5>
                                <p class="col-12 mb-0">Create a business directory entry</p>
                            </div>
                            <div v-else class="row">
                                <h5 class="modal-title col-12">Edit "{{ title || 'Business' }}"</h5>
                                <p class="col-12 mb-0">Edit business directory entry</p>
                            </div>
                        </span>
                    </div>
                </div>

                <form @submit.prevent.stop="storeBusinessDirectory">
                    <div class="modal-body" id="modal-body">
                        <div v-show="sendError" class="alert alert-danger" role="alert">Error: Business directory is not saved.</div>

                        <div class="row">
                            <div class="col" :class="mediaLibOpened ? 'col-8' : 'col-12'">
                                <div class="row">
                                    <div class="col-md-6">
                                        <div class="form-group mb-3">
                                            <label for="business-name" class="mb-2">Name</label>
                                            <input id="business-name" type="text" class="form-control" v-model="title" @input="onNameInput" required placeholder="Business name" />
                                        </div>
                                    </div>
                                    <div class="col-md-6">
                                        <div class="form-group mb-3">
                                            <label for="business-slug" class="mb-2">Slug</label>
                                            <input id="business-slug" type="text" class="form-control" v-model="slug" @input="onSlugInput" placeholder="business-slug" />
                                            <small v-if="checkingSlug" class="text-muted">Checking slug availability...</small>
                                            <small v-else-if="slug && slugAvailable" class="text-success">Slug is available.</small>
                                            <small v-else-if="slug && slugAvailable === false" class="text-danger">Slug already exists for this client.</small>
                                        </div>
                                    </div>
                                </div>

                                <div class="row">
                                    <div class="col-md-6">
                                        <div class="form-group mb-3">
                                            <label for="business-category" class="mb-2">Categories</label>
                                            <select id="business-category" class="form-control" v-model="selectedCategoryIds" multiple :size="Math.min(Math.max(categories.length + 1, 3), 8)">
                                                <option v-for="category in categories" :key="category.id" :value="category.id">{{ category.name }}</option>
                                            </select>
                                            <small class="text-muted">Hold Ctrl/Cmd to select multiple categories.</small>
                                        </div>
                                    </div>
                                    <div class="col-md-6">
                                        <div class="form-group mb-3">
                                            <label for="business-status" class="mb-2">Status</label>
                                            <select id="business-status" class="form-control" v-model="status" required>
                                                <option v-for="statusItem in statuses" :key="statusItem.value" :value="statusItem.value">{{ statusItem.label }}</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                <div class="row mb-3">
                                    <div class="col-md-6 mb-3 d-flex justify-content-center">
                                        <div class="tw-modal-featured-img d-flex align-items-center justify-content-center">
                                            <img v-if="featuredMediaPreview" :src="featuredMediaPreview" alt="Featured image" height="175" width="188" />
                                            <span v-else class="d-flex align-items-center justify-content-center">
                                                <span v-html="defaultImage"></span>
                                            </span>
                                        </div>
                                    </div>
                                    <div class="col-md-6 mb-3 d-flex justify-content-center align-items-center">
                                        <div class="d-flex flex-column gap-2">
                                            <button class="btn tw-grey-bg-color text-white" type="button" @click="mediaLib($event, chooseFeaturedImg)">
                                                <i class="fas fa-plus"></i>
                                                <span class="ms-2">Media Library</span>
                                            </button>
                                            <button v-if="featuredMediaId" class="btn tw-dark-grey-bg-color text-white" type="button" @click="removeFeaturedImage">
                                                Remove Image
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <div class="row">
                                    <div class="col-md-6">
                                        <div class="form-group mb-3">
                                            <label for="business-email" class="mb-2">Email</label>
                                            <input id="business-email" type="email" class="form-control" v-model="email" placeholder="name@example.com" />
                                        </div>
                                    </div>
                                    <div class="col-md-6">
                                        <div class="form-group mb-3">
                                            <label for="business-phone" class="mb-2">Phone</label>
                                            <input id="business-phone" type="text" class="form-control" v-model="phone" placeholder="Phone" />
                                        </div>
                                    </div>
                                </div>

                                <div class="row">
                                    <div class="col-12">
                                        <div class="form-group mb-3">
                                            <label for="business-address" class="mb-2">Address</label>
                                            <input id="business-address" type="text" class="form-control" v-model="address" placeholder="Address" />
                                        </div>
                                    </div>
                                </div>

                                <div class="row">
                                    <div class="col-12">
                                        <div class="form-group mb-3">
                                            <label for="business-website" class="mb-2">Website</label>
                                            <input id="business-website" type="text" class="form-control" v-model="website" placeholder="https://example.com" />
                                        </div>
                                    </div>
                                </div>

                                <div class="form-group mb-3">
                                    <div class="d-flex justify-content-between align-items-center mb-2">
                                        <label class="mb-0">Socials</label>
                                        <button class="btn btn-sm tw-dark-grey-bg-color text-white" type="button" @click="addSocial">
                                            <i class="fas fa-plus me-1"></i>Add Social
                                        </button>
                                    </div>
                                    <div v-if="socials.length" class="d-flex flex-column gap-2">
                                        <div v-for="(item, index) in socials" :key="`social-${index}`" class="row g-2 align-items-center">
                                            <div class="col-md-4">
                                                <input type="text" class="form-control" v-model="item.title" placeholder="Title (e.g. Facebook)" />
                                            </div>
                                            <div class="col-md-7">
                                                <input type="text" class="form-control" v-model="item.value" placeholder="Value (url, @handle, etc.)" />
                                            </div>
                                            <div class="col-md-1 text-end">
                                                <button class="btn btn-sm tw-light-red-bg-color text-white" type="button" @click="removeSocial(index)">
                                                    <i class="fas fa-trash"></i>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                    <small v-else class="text-muted">No socials added.</small>
                                </div>

                                <div class="form-group mb-3">
                                    <label class="mb-2">Content</label>
                                    <editor
                                        api-key=""
                                        v-model="content"
                                        theme="advanced"
                                        theme_advanced_toolbar_location="bottom"
                                        :init="tinymcInit"
                                    />
                                </div>
                            </div>

                            <MediaLibraryInModal ref="mediaLibrary" :mediaLibOpened="mediaLibOpened" @mediaLibClose="mediaLibOpened = false" @setMedia="handleSetFeaturedImage" />
                        </div>
                    </div>

                    <div class="modal-footer" :class="mediaLibOpened ? 'col-8' : 'col-12'">
                        <button class="btn tw-red-bg-color text-white" :disabled="!canSave">
                            Save
                            <span v-if="spinner"><i class="fas fa-spinner fa-spin"></i></span>
                        </button>
                        <button class="btn tw-dark-grey-bg-color text-white" type="button" @click="modal.hide()">Cancel</button>
                    </div>
                </form>
            </div>
        </div>
    </div>
</template>

<script>
import { Modal } from "bootstrap";
import editor from "@tinymce/tinymce-vue";
import tinymceConfig from "@/tinymce/config";
import MediaLibraryInModal from "@/components/media-library/MediaLibraryInModal";
import cms6Mixin from "@/mixins/cms6Mixin";

export default {
    name: "CreateBusinessDirectoryModal",
    components: {
        MediaLibraryInModal,
        editor,
    },
    emits: ["modalHidden", "directorySaved"],
    props: {
        editing: Boolean,
        editingDirectory: {
            type: Object,
            default: null,
        },
    },
    data: () => ({
        modal: null,
        spinner: false,
        sendError: false,
        categories: [],
        title: "",
        slug: "",
        content: "",
        selectedCategoryIds: [],
        status: "publish",
        statuses: [
            { label: "Public", value: "publish" },
            { label: "Draft", value: "draft" },
        ],
        email: "",
        phone: "",
        address: "",
        website: "",
        socials: [],
        slugTouched: false,
        slugAvailable: null,
        checkingSlug: false,
        slugCheckTimer: null,
        defaultImage: '<div class="tw-defaul-featured-img tw-light-grey-bg-color d-flex align-items-center"><i class="fas fa-image me-2"></i> <span>Select from Media Library</span></div>',
        featuredMedia: null,
        featuredMediaId: null,
        featuredMediaPreview: null,
        mediaLibOpened: false,
        chooseFeaturedImg: true,
    }),
    computed: {
        canSave() {
            return !!this.title && !!this.slug && this.slugAvailable === true && !this.checkingSlug && !this.spinner;
        },
        tinymcInit() {
            return tinymceConfig.config;
        },
    },
    mounted() {
        this.modal = new Modal(this.$refs.createBusinessDirectoryModal);

        const modalElement = document.getElementById("createBusinessDirectoryModal");
        modalElement.addEventListener("hidden.bs.modal", () => {
            this.$emit("modalHidden");
            this.resetForm();
        });
    },
    watch: {
        editingDirectory: {
            handler(newVal) {
                if (newVal) {
                    this.title = newVal.name || "";
                    this.slug = newVal.slug || "";
                    this.content = newVal.content || "";
                    this.selectedCategoryIds = this.normalizeCategoryIds(newVal);
                    this.status = ["publish", "draft"].includes(newVal.status) ? newVal.status : "draft";
                    this.email = newVal.email || "";
                    this.phone = newVal.phone || "";
                    this.address = newVal.address || "";
                    this.website = newVal.website || "";
                    this.socials = Array.isArray(newVal.socials)
                        ? newVal.socials.map((item) => ({
                              title: item?.title || "",
                              value: item?.value || "",
                          }))
                        : [];
                    this.slugTouched = true;

                    const mediaObject =
                        (newVal.featured_media && typeof newVal.featured_media === "object" && newVal.featured_media) ||
                        (newVal.featured_image && typeof newVal.featured_image === "object" && newVal.featured_image) ||
                        (newVal.featuredImage && typeof newVal.featuredImage === "object" && newVal.featuredImage) ||
                        null;

                    if (mediaObject) {
                        this.featuredMedia = mediaObject;
                        this.featuredMediaId = mediaObject.id || null;
                        this.featuredMediaPreview = this.getMediaPreviewUrl(mediaObject);
                    } else {
                        this.featuredMedia = null;
                        this.featuredMediaId = newVal.featured_media || newVal.featured_image || null;
                        this.featuredMediaPreview = null;
                    }

                    this.scheduleSlugCheck();
                }
            },
            deep: true,
        },
        slug() {
            this.scheduleSlugCheck();
        },
    },
    methods: {
        getMediaPreviewUrl(media) {
            if (!media || typeof media !== "object") {
                return null;
            }

            if (media.media_details?.sizes?.thumbnail?.source_url) {
                return media.media_details.sizes.thumbnail.source_url;
            }

            return media.source_url || null;
        },
        async showModal(value) {
            if (!value) {
                return;
            }
            await this.setCategories();
            if (!this.editing) {
                this.slugTouched = false;
                this.slugAvailable = null;
            }
            this.modal.show();
        },
        async setCategories() {
            const res = await this.getBusinessDirectoryCategories();
            this.categories = res && res.status === 200 ? res.data || [] : [];
        },
        addSocial() {
            this.socials.push({ title: "", value: "" });
        },
        removeSocial(index) {
            this.socials.splice(index, 1);
        },
        normalizeCategoryIds(directory = null) {
            const rawIds = Array.isArray(directory?.categories)
                ? directory.categories.map((category) => (typeof category === "object" ? category?.id : category))
                : Array.isArray(directory?.category_ids)
                  ? directory.category_ids
                  : directory?.business_category_id
                    ? [directory.business_category_id]
                    : [];

            return rawIds
                .map((categoryId) => Number(categoryId))
                .filter((categoryId, index, ids) => Number.isInteger(categoryId) && categoryId > 0 && ids.indexOf(categoryId) === index);
        },
        normalizedSocials() {
            return this.socials
                .map((item) => ({
                    title: (item?.title || "").trim(),
                    value: (item?.value || "").trim(),
                }))
                .filter((item) => item.title || item.value);
        },
        onNameInput() {
            if (!this.slugTouched) {
                this.slug = this.slugify(this.title);
            }
        },
        onSlugInput() {
            this.slugTouched = true;
            this.slug = this.slugify(this.slug);
        },
        slugify(text) {
            return (text || "")
                .toString()
                .toLowerCase()
                .trim()
                .replace(/[^a-z0-9\s-]/g, "")
                .replace(/\s+/g, "-")
                .replace(/-+/g, "-");
        },
        scheduleSlugCheck() {
            if (this.slugCheckTimer) {
                clearTimeout(this.slugCheckTimer);
            }

            const normalizedSlug = this.slugify(this.slug);
            if (normalizedSlug !== this.slug) {
                this.slug = normalizedSlug;
                return;
            }

            if (!this.slug) {
                this.slugAvailable = false;
                this.checkingSlug = false;
                return;
            }

            this.checkingSlug = true;
            this.slugCheckTimer = setTimeout(() => {
                this.checkSlugAvailability();
            }, 350);
        },
        async checkSlugAvailability() {
            try {
                const excludeId = this.editing && this.editingDirectory?.id ? this.editingDirectory.id : null;
                const res = await this.checkBusinessDirectorySlugAvailability(this.slug, excludeId);
                this.slugAvailable = !!(res && res.data && res.data.available);
            } catch (error) {
                this.slugAvailable = false;
            } finally {
                this.checkingSlug = false;
            }
        },
        mediaLib(event, chooseFeaturedImg = false) {
            if (this.mediaLibOpened) {
                this.mediaLibOpened = false;
            } else {
                const allowMulti = !chooseFeaturedImg;
                this.$refs.mediaLibrary.mediaLibOpen(true, chooseFeaturedImg, allowMulti, chooseFeaturedImg);
                this.mediaLibOpened = true;
            }
        },
        handleSetFeaturedImage(image) {
            this.featuredMedia = image;
            this.featuredMediaId = image ? image.id : null;
            this.featuredMediaPreview = image && image.media_details && image.media_details.sizes && Object.keys(image.media_details.sizes).length
                ? image.media_details.sizes.thumbnail.source_url
                : image
                  ? image.source_url
                  : null;
        },
        removeFeaturedImage() {
            this.featuredMedia = null;
            this.featuredMediaId = null;
            this.featuredMediaPreview = null;
        },
        resetForm() {
            this.spinner = false;
            this.sendError = false;
            this.title = "";
            this.slug = "";
            this.content = "";
            this.selectedCategoryIds = [];
            this.status = "publish";
            this.email = "";
            this.phone = "";
            this.address = "";
            this.website = "";
            this.socials = [];
            this.slugTouched = false;
            this.slugAvailable = null;
            this.checkingSlug = false;
            this.featuredMedia = null;
            this.featuredMediaId = null;
            this.featuredMediaPreview = null;
            this.mediaLibOpened = false;

            if (this.slugCheckTimer) {
                clearTimeout(this.slugCheckTimer);
                this.slugCheckTimer = null;
            }
        },
        async storeBusinessDirectory() {
            if (!this.canSave) {
                return;
            }

            this.spinner = true;
            this.sendError = false;

            const payload = {
                name: this.title,
                slug: this.slug || null,
                content: this.content || null,
                categories: this.selectedCategoryIds,
                business_category_id: this.selectedCategoryIds[0] || null,
                status: this.status,
                featured_media: this.featuredMediaId,
                socials: this.normalizedSocials(),
                email: this.email || null,
                phone: this.phone || null,
                address: this.address || null,
                website: this.website || null,
            };

            let res;
            if (this.editing && this.editingDirectory?.id) {
                payload.id = this.editingDirectory.id;
                res = await this.updateBusinessDirectory(payload);
            } else {
                res = await this.saveBusinessDirectory(payload);
            }

            this.spinner = false;

            if (res && (res.status === 200 || res.status === 201)) {
                const action = this.editing ? "updated" : "created";
                this.modal.hide();
                this.$emit("directorySaved", action);
            } else {
                this.sendError = true;
            }
        },
    },
    mixins: [cms6Mixin],
};
</script>
